<template>
  <div class="l-main">
    <div class="c-language-bar">
      <span class="c-language-bar__label">Language</span>
      <app-tabs
        variant="pills"
        label="Language"
        :tabs="languageTabs"
        :value="currentLanguage.code"
        @input="changeLanguageByCode"
      />
    </div>

    <div class="l-main__ctn">
      <div
        class="c-filter-bar"
      >
        <label class="c-filter-bar__field c-filter-bar__field--wide">
          <span>Search</span>
          <span class="ui-field__control">
            <app-icon
              class="ui-field__icon"
              name="search"
            />
            <input
              v-model="searchQuery"
              class="ui-field__input"
              type="text"
              placeholder="Name or ID (min. 3 characters)"
              @keydown.enter="searchSeo(true)"
            >
          </span>
        </label>

        <label class="c-filter-bar__field">
          <span>Search in</span>
          <select
            v-model="selectedFilter"
            class="ui-select"
            @change="filterSelectedEvent()"
          >
            <option
              v-for="filter in availableFilters"
              :key="filter.value"
              :value="filter.value"
            >
              {{ filter.label }}
            </option>
          </select>
        </label>

        <template v-if="selectedFilter === 'region' || selectedFilter === 'league'">
          <label class="c-filter-bar__field">
            <span>Sport name</span>
            <input
              v-model="searchSportQuery"
              class="ui-field__input"
              type="text"
              placeholder="Optional"
              @keydown.enter="searchSeo(true)"
            >
          </label>
          <label
            v-if="selectedFilter === 'league'"
            class="c-filter-bar__field"
          >
            <span>Region name</span>
            <input
              v-model="searchRegionQuery"
              class="ui-field__input"
              type="text"
              placeholder="Optional"
              @keydown.enter="searchSeo(true)"
            >
          </label>
        </template>

        <button
          type="button"
          class="ui-btn ui-btn--primary"
          :disabled="searchQuery.length < 3"
          :title="searchQuery.length < 3 ? 'Enter at least 3 characters' : null"
          @click="searchSeo(true)"
        >
          <app-icon name="search" />
          Search
        </button>
      </div>

      <div class="c-table-wrapper l-seo-rules__table">
        <base-table-component
          :prop-data="seoRulesTableData"
          sortable
          :empty-title="fetchedResults ? 'No SEO rules found' : 'Search SEO rules'"
          :empty-description="fetchedResults ? 'Try another name, ID or “Search in” option.' : 'Enter a name or ID above to find SEO rules.'"
          @sort="handleSort"
        >
          <template v-slot:default="slotProps">
            <span class="c-table__btn-wrap">
              <generic-button
                icon
                transparent
                text-inherit
                @click="editSeoRule(slotProps.selected.seoRule)"
              >
                <md-tooltip md-direction="top">
                  Edit SEO rule
                </md-tooltip>
                <img src="../../../../../assets/edit-button.svg" alt="">
              </generic-button>
            </span>
          </template>
        </base-table-component>
      </div>

      <paginate
        v-if="totalPages > 1"
        v-model="currentPage"
        :page-count="totalPages"
        :prev-text="'‹'"
        :next-text="'›'"
        :page-range="5"
        :margin-pages="1"
        :break-view-link-class="'md-button'"
        :click-handler="searchSeo"
        :container-class="'c-pagination'"
        :prev-link-class="'md-button prev md-elevation-1'"
        :next-link-class="'md-button next md-elevation-1'"
        :page-link-class="'md-button md-elevation-1'"
        :active-class="'active'"
        :disabled-class="'disabled'"
      />
    </div>
  </div>
</template>

<script>
import GenericButton from '../../../../../shared/buttons/GenericButton';
import AppIcon from '@/shared/app-icon/AppIcon';
import AppTabs from '@/shared/app-tabs/AppTabs';
import { jsonParse } from '@/helpers/transformers'
import { seoRulesService } from '@/services/seo-rules'
import get from 'lodash/get'
import { languageService } from '@/services/language'
import BaseTableComponent from '@/shared/table/BaseTableComponent'
import EditSeoRuleModal from './edit'
import { seoRulesTargetTypeMapper } from '@/const/seo-rules'

export default {
  name: "SeoRules",

  components: {
    GenericButton,
    BaseTableComponent,
    AppIcon,
    AppTabs,
  },

  props: {
    website: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      searchQuery: "",
      searchSportQuery: "",
      searchRegionQuery: "",
      selectedFilter: "",
      searchItems: [],
      fetchedResults: false,
      languages: [],
      currentLanguage: {},
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      defaultLanguageId: null,
      availableFilters: [
        { label: 'All', value: '' },
        { label: 'Sports', value: 'sport' },
        { label: 'Early payout', value: 'sport/earlyPayout' },
        { label: 'Regions', value: 'region' },
        { label: 'Leagues', value: 'league' },
        { label: 'Pages', value: 'page' },
      ],
      sortColumnActive: '',
      sortColumnDirection: '',
    };
  },

  computed: {
    languageTabs() {
      return this.languages.map(language => ({ key: language.code, label: String(language.code).toUpperCase() }));
    },

    seoRulesTableData() {
      return this.searchItems.map(seoRule => ({
        seoRule: seoRule,
        rowData: {
          name: seoRule.translation,
          ...this._getEntities(seoRule),
          id: this._getEntityId(seoRule),
        }
      }));
    },

    selectedFilterSplit() {
      const [value, altCtx] = this.selectedFilter.split('/');
      return { value, altCtx };
    }
  },

  created() {
    this.getAvailableLanguages();
  },

  methods: {
    async searchSeo(page = 1) {
      if (this.searchQuery.length < 3) {
        this.searchItems = []
        this.totalPages = 1;

        this.fetchedResults = false;
        return;
      }

      this.currentPage = typeof page !== 'number' ? 1 : page;

      const searchConstraints = {
        ...(this.searchQuery ? { term: this.searchQuery} : {}),
        ...(this.searchSportQuery ? { sport: this.searchSportQuery} : {}),
        ...(this.searchRegionQuery ? { region: this.searchRegionQuery} : {}),
        ...(this.selectedFilterSplit.value ? { filter: this.selectedFilterSplit.value } : {}),
        ...(this.currentPage ? { page: this.currentPage} : {}),
        ...(this.defaultLanguageId ? { defaultLanguageId: this.languages.map(language => language.id).join(',')} : {}),
        ...(this.sortColumnActive ? { sortColumn: this.sortColumnActive } : {}),
        ...(this.sortColumnDirection ? { sortDirection: this.sortColumnDirection } : {})
      }

      const searchResponse = await seoRulesService.search(this.currentLanguage.id, searchConstraints)

      const {items, pageCount} = searchResponse.data;

      this.searchItems = items;
      this.totalPages = pageCount;

      this.fetchedResults = true;
    },

    changeLanguageByCode(code) {
      this.changeLanguage(this.languages.find(language => language.code === code));
    },

    changeLanguage(language) {
      this.currentLanguage = language;
      this.searchSeo();
    },

    async getAvailableLanguages() {
      const allLanguagesResponse = await languageService.getAllOnlineLanguages();
      const allOnlineLanguages = allLanguagesResponse.data.items;

      const additionalLanguages = jsonParse(get(this, 'website.betshop.market.additionalLanguages')) || [];
      const defaultLanguage = get(this, 'website.betshop.market.defaultLanguage', 'en');

      const betshopLanguageCodes = [...new Set([defaultLanguage, ...additionalLanguages])];

      this.languages = allOnlineLanguages.filter(onlineLanguage => betshopLanguageCodes.includes(onlineLanguage.code))
      this.currentLanguage = this.languages[0];
      this.defaultLanguageId = this.languages.find(language => language.code === 'en')?.id
    },

    editSeoRule(seoRule) {
      this.$modal.show(
        EditSeoRuleModal,
        {
          seoRule,
          website: this.website,
          language: this.currentLanguage,
          ...(this.selectedFilterSplit.altCtx ? { altCtx: this.selectedFilterSplit.altCtx } : {}),
        },
        { height: 'auto', scrollable: true, width: '80%' }
      );
    },

    filterSelectedEvent() {
      this.searchRegionQuery = "";
      this.searchSportQuery = "";
      this.searchSeo();
    },

    // rules can come without `target` (or a partial one): show '-' instead of breaking the whole table
    _getEntities(seoRule) {
      const entities = {
        sport: '',
        region: '',
        type: '',
      }

      entities.type = seoRulesTargetTypeMapper[seoRule.entityType];

      switch (seoRule.entityType) {
        case 'SportEntity':
          entities.sport = get(seoRule, 'target.name', '-');
          break;
        case 'RegionEntity':
          entities.region = get(seoRule, 'target.name', '-');
          entities.sport = get(seoRule, 'target.sport.name', '-');
          break;
        case 'LeagueEntity':
          entities.sport = get(seoRule, 'target.region.sport.name', '-');
          entities.region = get(seoRule, 'target.region.name', '-');
          break;
      }

      return entities;
    },

    _getEntityId(seoRule) {
      switch (seoRule.entityType) {
        case 'SportEntity':
          return get(seoRule, 'target.sportId', '-');
        case 'RegionEntity':
          return get(seoRule, 'target.regionId', '-');
        case 'LeagueEntity':
          return get(seoRule, 'target.leagueId', '-');
        default:
          return '-'
      }
    },

    handleSort(column, direction) {
      this.sortColumnActive = column;
      this.sortColumnDirection = direction;
      this.searchSeo(this.currentPage);
    }
  },
};
</script>

<style lang="scss" scoped>
.l-main {
  display: flex;
  flex-direction: column;
}
</style>
